if (!customElements.get('recipient-form')) {
  customElements.define('recipient-form', class RecipientForm extends HTMLElement {
    constructor() {
      super();

      // Drawer form field selectors
      this.emailInput = this.querySelector(`#RecipientDrawer-email`);
      this.nameInput = this.querySelector(`#RecipientDrawer-name`);
      this.messageInput = this.querySelector(`#RecipientDrawer-message`);
      this.sendonInput = this.querySelector(`#RecipientDrawer-send-on`);
      this.offsetProperty = this.querySelector(`#RecipientDrawer-timezone-offset`);
      this.hiddenControlField = this.querySelector(`#RecipientDrawer-control`);

      if (this.offsetProperty) {
        this.offsetProperty.value = new Date().getTimezoneOffset();
      }

      // Enable all fields by default (drawer mode)
      this.enableInputFields();

      // Set the control flag to enable recipient form
      if (this.hiddenControlField) {
        this.hiddenControlField.value = 'true';
        this.hiddenControlField.disabled = false;
      }

      // Initialize floating labels
      this.#initFloatingLabels();
    }

    cartUpdateUnsubscriber = undefined;
    cartErrorUnsubscriber = undefined;

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form') {
          this.resetRecipientForm();
        }
      });

      this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event.source === 'product-form') {
          this.displayErrorMessage(event.message, event.errors);
        }
      });

      // Update floating labels on input
      this.#attachFloatingLabelListeners();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }

      if (this.cartErrorUnsubscriber) {
        this.cartErrorUnsubscriber();
      }
    }

    inputFields() {
      return [
        this.emailInput,
        this.nameInput,
        this.messageInput,
        this.sendonInput
      ].filter(Boolean);
    }

    disableableFields() {
      return [...this.inputFields(), this.offsetProperty].filter(Boolean);
    }

    clearInputFields() {
      this.inputFields().forEach((field) => {
        if (field) {
          field.value = '';
          this.#updateFloatingLabel(field);
        }
      });
    }

    enableInputFields() {
      this.disableableFields().forEach((field) => {
        if (field) field.disabled = false;
      });
    }

    displayErrorMessage(title, body) {
      this.clearErrorMessage();
      if (typeof body === 'object') {
        return Object.entries(body).forEach(([key, value]) => {
          const errorMessageId = `RecipientFormDrawer-${key}-error`;
          const message = `${value.join(', ')}`;
          const errorMessageElement = this.querySelector(`#${errorMessageId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message');
          if (!errorTextElement) return;

          errorTextElement.innerText = `${message}.`;
          errorMessageElement.classList.remove('hidden');

          const inputElement = this[`${key}Input`];
          if (!inputElement) return;

          inputElement.setAttribute('aria-invalid', true);
          inputElement.setAttribute('aria-describedby', errorMessageId);
        });
      }
    }

    clearErrorMessage() {
      this.querySelectorAll('.recipient-fields .form__message').forEach(field => {
        field.classList.add('hidden');
        const textField = field.querySelector('.error-message');
        if (textField) textField.innerText = '';
      });

      [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach(inputElement => {
        if (inputElement) {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        }
      });

      this.clearConsentError();
    }

    displayConsentError(message) {
      const consentCheckbox = this.querySelector('#RecipientDrawer-consent');
      const errorElement = this.querySelector('#RecipientFormDrawer-consent-error');
      const errorTextElement = errorElement?.querySelector('.error-message');

      if (!consentCheckbox || !errorTextElement) return;

      const errorMessage = message || this.dataset.consentErrorMessage || 'Please accept the terms to continue.';
      errorTextElement.innerText = errorMessage;
      errorElement.classList.remove('hidden');
      consentCheckbox.setAttribute('aria-invalid', 'true');
      consentCheckbox.setAttribute('aria-describedby', 'RecipientFormDrawer-consent-error');
    }

    clearConsentError() {
      const consentCheckbox = this.querySelector('#RecipientDrawer-consent');
      const errorElement = this.querySelector('#RecipientFormDrawer-consent-error');
      const errorTextElement = errorElement?.querySelector('.error-message');

      if (errorTextElement) {
        errorTextElement.innerText = '';
        errorElement.classList.add('hidden');
      }

      if (consentCheckbox) {
        consentCheckbox.setAttribute('aria-invalid', 'false');
        consentCheckbox.removeAttribute('aria-describedby');
      }
    }

    resetRecipientForm() {
      this.clearInputFields();
      this.clearErrorMessage();

      const consentCheckbox = this.querySelector('#RecipientDrawer-consent');
      if (consentCheckbox) consentCheckbox.checked = false;
    }

    validateMandatoryConsent() {
      const consentCheckbox = this.querySelector('#RecipientDrawer-consent');

      if (consentCheckbox && consentCheckbox.hasAttribute('required')) {
        if (!consentCheckbox.checked) {
          this.displayConsentError();
          consentCheckbox.focus();
          return false;
        }
        this.clearConsentError();
      }

      return true;
    }

    #initFloatingLabels() {
      this.inputFields().forEach((field) => {
        if (field) {
          this.#updateFloatingLabel(field);
        }
      });
    }

    #attachFloatingLabelListeners() {
      this.inputFields().forEach((field) => {
        if (field) {
          field.addEventListener('input', () => {
            this.#updateFloatingLabel(field);
          });
          field.addEventListener('change', () => {
            this.#updateFloatingLabel(field);
          });
          field.addEventListener('blur', () => {
            this.#updateFloatingLabel(field);
          });
        }
      });
    }

    #updateFloatingLabel(field) {
      let hasValue = false;

      if (field.type === 'date') {
        hasValue = field.value !== '';
      } else {
        hasValue = field.value && field.value.trim() !== '';
      }

      const fieldElement = field.closest('.field');

      if (!fieldElement) return;

      if (hasValue) {
        fieldElement.classList.add('has-value');
      } else {
        fieldElement.classList.remove('has-value');
      }
    }

  });
}
